import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { Beneficiary } from '../../../types/customer-portal';
import { validateNameField, filterNameField, NAME_FIELD_MAX_LENGTH } from '../../../../../shared/utils/fieldValidation';
import './BeneficiaryModal.css';

import { useRelationships } from '@/portals/customer/hooks/useRelationships';

interface BeneficiaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (beneficiary: Beneficiary) => void;
  onDelete?: (beneficiaryId: string) => void;
  beneficiary?: Beneficiary | null;
  isEditMode?: boolean;
}

const BeneficiaryModal: React.FC<BeneficiaryModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  beneficiary,
  isEditMode = false
}) => {
  const [formData, setFormData] = useState<Omit<Beneficiary, 'id'>>({
    title: '',
    firstName: '',
    middleName: '',
    lastName: '',
    suffix: 'N/A',
    relationship: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens/closes or beneficiary changes
  useEffect(() => {
    if (isOpen) {
      if (beneficiary && isEditMode) {
        setFormData({
          title: beneficiary.title,
          firstName: beneficiary.firstName,
          middleName: beneficiary.middleName || '',
          lastName: beneficiary.lastName,
          suffix: beneficiary.suffix,
          relationship: beneficiary.relationship
        });
      } else {
        setFormData({
          title: '',
          firstName: '',
          middleName: '',
          lastName: '',
          suffix: 'N/A',
          relationship: ''
        });
      }
      setErrors({});
    }
  }, [isOpen, beneficiary, isEditMode]);

    // Use the custom hook to fetch nationalities
    const { relationshipsOptions, loading: relationshipsLoading, error: relationshipsError } = useRelationships();

  const handleInputChange = (field: keyof Omit<Beneficiary, 'id'>, value: string) => {
    let processedValue = value;
    
    // Apply field-specific filtering for name fields
    if (field === 'firstName' || field === 'lastName' || field === 'middleName') {
      processedValue = filterNameField(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title || formData.title.trim() === '') {
      newErrors.title = 'Title is required';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.relationship || formData.relationship === '') {
      newErrors.relationship = 'Relationship is required';
    }

    // Add pattern validation for name fields
    if (formData.firstName && !validateNameField(formData.firstName)) {
      newErrors.firstName = 'First name contains invalid characters';
    }
    
    if (formData.lastName && !validateNameField(formData.lastName)) {
      newErrors.lastName = 'Last name contains invalid characters';
    }
    
    if (formData.middleName && !validateNameField(formData.middleName)) {
      newErrors.middleName = 'Middle name contains invalid characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const beneficiaryData: Beneficiary = {
      id: beneficiary?.id || Date.now().toString(),
      ...formData
    };

    onSave(beneficiaryData);
    onClose();
  };

  const handleDelete = () => {
    if (beneficiary && onDelete) {
      onDelete(beneficiary.id);
      onClose();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="beneficiary-modal-backdrop" onClick={handleBackdropClick}>
      <div className="beneficiary-modal-container">
        {/* Modal Header */}
        <div className="beneficiary-modal-header">
          <div className="beneficiary-modal-title-section">
            <h2 className="beneficiary-modal-title">Beneficiary Information</h2>
            <p className="beneficiary-modal-subtitle">
              Please provide the personal details of the of the person you'd like to name as your beneficiary. 
            </p>
          </div>
          <button
            className="beneficiary-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Modal Content */}
        <div className="beneficiary-modal-content">
          <div className="beneficiary-form beneficiary-form-non-group">
            {/* Name Fields Row */}
            <div className="form-row name-row">
              <div className="form-group title-group">
                <label className="form-label">Title *</label>
                <select
                  className={`form-select ${errors.title ? 'error' : ''}`}
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                >
                  <option value="">Title</option>
                  <option value="Ms.">Ms.</option>
                  <option value="Mrs.">Mrs.</option>
                  <option value="Mr.">Mr.</option>
                </select>
                {errors.title && <span className="error-message">{errors.title}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">First Name *</label>
                <input
                  type="text"
                  className={`form-input ${errors.firstName ? 'error' : ''}`}
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  placeholder=""
                  maxLength={NAME_FIELD_MAX_LENGTH}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Middle Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.middleName}
                  onChange={(e) => handleInputChange('middleName', e.target.value)}
                  placeholder=""
                  maxLength={NAME_FIELD_MAX_LENGTH}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Last Name *</label>
                <input
                  type="text"
                  className={`form-input ${errors.lastName ? 'error' : ''}`}
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  placeholder=""
                  maxLength={NAME_FIELD_MAX_LENGTH}
                />
              </div>

              <div className="form-group suffix-group">
                <label className="form-label">Suffix</label>
                <select
                  className="form-select"
                  value={formData.suffix}
                  onChange={(e) => handleInputChange('suffix', e.target.value)}
                >
                  <option value="N/A">N/A</option>
                  <option value="Jr.">Jr.</option>
                  <option value="Sr.">Sr.</option>
                  <option value="II">II</option>
                  <option value="III">III</option>
                </select>
              </div>
            </div>
            {/* Relationship Field */}
            <div className="form-row">
              <div className="form-group relationship-group">
                <label className="form-label">Relationship to Principal Insured *</label>
                <select
                  className={`form-select ${errors.relationship ? 'error' : ''}`}
                  value={formData.relationship}
                  onChange={(e) => handleInputChange('relationship', e.target.value)}
                  disabled={relationshipsLoading}
                >
                  <option value="">
                    {relationshipsLoading ? 'Loading relationships...' : 'Choose Relationships'}
                  </option>
                  {relationshipsError && (
                    <option value="" disabled>
                      Error loading relationships
                    </option>
                  )}
                  {relationshipsOptions.map((relationship) => (
                    <option key={relationship} value={relationship}>
                      {relationship}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="beneficiary-modal-footer">
          {isEditMode && onDelete ? (
            <div className="beneficiary-modal-footer-left">
              <button
                className="beneficiary-modal-delete-btn"
                onClick={handleDelete}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="delete-icon"
                >
                  <path
                    d="M3 6H5H21"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10 11V17"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14 11V17"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Delete Beneficiary
              </button>
              <div className='beneficiary-modal-delete-warning-position'>
                <p className="beneficiary-modal-delete-warning ">
                  By clicking "Delete" will permanently remove this beneficiary.
                </p>          
              </div>              
            </div>
          ) : (
            <div></div> // Empty spacer div when no delete button
          )}

          <div className="beneficiary-modal-footer-actions">
            <button
              className="beneficiary-modal-btn-cancel"
              onClick={onClose}
            >
              CANCEL
            </button>
            <button
              className="beneficiary-modal-btn-save"
              onClick={handleSave}
            >
              SAVE
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Render modal using React portal to ensure it's rendered at document body level
  return createPortal(modalContent, document.body);
};

export default BeneficiaryModal;